import { Button } from '@mui/material'
import { useState } from 'react'
import { Fade, IconButton, InformationModal } from 'utoniq-ui'

import {
  Avatar,
  ItemDetailBoxFragment,
  Maybe,
  User,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ItemImageArea, SupplementLinkArea } from '@/components/organisms'
import { SkeletonLoader } from '@/components/pages/OwnedItemDetail/template/components/SkeletonLoader'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualHiddenItemInfo } from '@/core/individual'
import { encodeSkuId } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  isLoggedIn: boolean
  hasItem: boolean
  hasNextPage: boolean
  disabledTrade: boolean
  userId: string
  itemId: string
  item: ItemDetailBoxFragment | null
  posessionNumber: number
  user:
    | (Pick<User, 'id'> & {
        avatar?: Maybe<Pick<Avatar, 'uri' | 'smallUri'>> | undefined
      })
    | null
    | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const UserItemDetailTemplate: Template<State, Action> = ({ state }) => {
  const { routerNames, routerPages, generatePath } = useRouter()
  const { t } = useTranslation('userItemDetail')
  const { onClick } = useBrowserLink(getHelpUrl('ownedItemDetail'))

  return (
    <ArtistThemeProvider artistId={state.item?.issuedArtist?.id}>
      <Head title={state.item?.title || routerNames.UserItemDetail} />

      <BaseLayout
        header={{
          back: true,
          type: 'modal',
          defaultBackPage: generatePath(routerPages.UserOwnedItems, {
            userId: state.userId ?? '#',
          }),
          right: (
            <IconButton.Help
              edge="end"
              color="primary.main"
              size="small"
              onClick={onClick}
            />
          ),
        }}
      >
        <Fade style={{ height: '100%' }}>
          {state.loading ? (
            <SkeletonLoader />
          ) : (
            <UserItemDetailCell
              item={state.item}
              userId={state.userId}
              itemId={state.itemId}
              loading={state.loading}
              hasItem={state.hasItem}
              disabledTrade={state.disabledTrade}
              posessionNumber={state.posessionNumber}
              avatarUri={
                state.user?.avatar?.smallUri ?? state.user?.avatar?.uri ?? ''
              }
            />
          )}
        </Fade>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type UserItemDetailCellProps = {
  item: State['item']
  userId: string
  itemId: string
  loading: boolean
  hasItem: boolean
  disabledTrade: boolean
  posessionNumber: number
  avatarUri: string
}
const UserItemDetailCell: Cell<UserItemDetailCellProps> = ({
  item,
  userId,
  itemId,
  loading,
  hasItem,
  disabledTrade,
  posessionNumber,
  avatarUri,
}) => {
  const { t } = useTranslation('userItemDetail')
  const { handleLoginModal } = usePrivateAction()
  const { routerPages, generatePath, router } = useRouter()
  const [openNotTradeConfirm, setOpenNotTradeConfirm] = useState<boolean>(false)

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <ItemImageArea
        item={item}
        loading={loading}
        noItem={!hasItem}
        possessionNumber={posessionNumber ?? 0}
        avatarUri={avatarUri}
      />
      <SupplementLinkArea
        itemId={itemId}
        artistId={item?.issuedArtist?.id ?? ''}
        startItems={[
          {
            type: 'itemInfo',
            hidden: individualHiddenItemInfo(),
            onClick: handleLoginModal(),
            href: `${generatePath(
              userId
                ? routerPages.UserItemInformation
                : routerPages.OwnedItemInformation,
              {
                itemId,
                userId,
              }
            )}${item?.sku ? `?skuId=${encodeSkuId(item?.sku.id)}` : ''}`,
          },
          {
            type: 'trade',
            hidden: !!item?.hideSalesNumber,
            onClick: handleLoginModal(() => {
              if (disabledTrade) {
                setOpenNotTradeConfirm(true)
                return
              }
              router.push(
                generatePath(routerPages.SerialNumberSelect, {
                  itemId,
                  userId,
                })
              )
            }),
            active: !disabledTrade,
          },
        ]}
        endItems={[{ type: 'comment', title: item?.commentCount ?? 0 }]}
        pageName="user_item_detail"
      />
      <InformationModal
        open={openNotTradeConfirm}
        onClose={() => setOpenNotTradeConfirm(false)}
        title={t('notTradeConfirmTitle')}
        content={
          item?.sku ? t('notTradeConfirmSKUDesc') : t('notTradeConfirmDesc')
        }
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            onClick={() => setOpenNotTradeConfirm(false)}
          >
            OK
          </Button>
        }
      />
    </CellFrame>
  )
}
